import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getActiveLocale,
  setActiveLocale,
  SUPPORTED_LOCALES
} from "../i18n";
import { SelectionModal } from "./SelectionModal";
import { createLogger } from "../utils/logger";

const logger = createLogger("LocaleSwitcher");

const LOCALE_FLAGS: Record<string, string> = {
  "en-US": "🇺🇸",
  "fil-PH": "🇵🇭"
};

interface LocaleSwitcherProps {
  className?: string;
}

export function LocaleSwitcher({ className }: LocaleSwitcherProps) {
  const { t } = useTranslation();
  const [activeLocale, setActiveLocaleState] = useState(() => getActiveLocale());
  const [isPickerOpen, setPickerOpen] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const firstOptionRef = useRef<HTMLButtonElement>(null);

  const options = useMemo(
    () =>
      SUPPORTED_LOCALES.map((locale) => ({
        value: locale.code,
        label: t(locale.labelKey, { defaultValue: locale.nativeLabel })
      })),
    [t]
  );

  const currentOption = options.find(
    (option) => option.value === activeLocale
  );
  const currentLabel = currentOption?.label ?? activeLocale;

  const closePicker = (refocus = true) => {
    setPickerOpen(false);
    if (refocus && triggerRef.current) {
      setTimeout(() => triggerRef.current?.focus(), 0);
    }
  };

  useEffect(() => {
    if (isPickerOpen && firstOptionRef.current) {
      firstOptionRef.current.focus();
    }
  }, [isPickerOpen]);

  const handleChange = async (value: string) => {
    if (value === activeLocale || isUpdating) {
      return;
    }
    try {
      setIsUpdating(true);
      await setActiveLocale(value);
      setActiveLocaleState(value);
      closePicker();
    } catch (error) {
      logger.error("Failed to change locale", error);
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        className={`pm-btn pm-btn-secondary pm-btn-sm ${className ?? ""}`}
        onClick={() => setPickerOpen(true)}
        disabled={isUpdating}
        aria-haspopup="dialog"
        aria-expanded={isPickerOpen}
        aria-label={t("site_footer.language.aria", { language: currentLabel })}
      >
        <span aria-hidden="true">
          {LOCALE_FLAGS[activeLocale] ?? "🏳"}
        </span>
        <span>{currentLabel}</span>
      </button>

      {isPickerOpen && (
        <SelectionModal
          title={t("site_footer.language.label")}
          closeLabel={t("actions.close")}
          onClose={() => closePicker()}
        >
          <ul className="flex max-h-[320px] flex-col gap-2 overflow-y-auto">
            {options.map((option, index) => {
              const isActive = option.value === activeLocale;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    ref={index === 0 ? firstOptionRef : undefined}
                    className={`flex w-full items-center justify-between rounded-xl border border-slate-200 px-4 py-3 text-left text-sm font-semibold text-slate-700 transition hover:border-slate-400 focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-500 ${
                      isActive ? "bg-slate-100 border-slate-400" : "bg-white"
                    }`}
                    data-locale={option.value}
                    onClick={() => handleChange(option.value)}
                    aria-current={isActive}
                  >
                    <span className="flex items-center gap-3">
                      <span className="text-lg" aria-hidden="true">
                        {LOCALE_FLAGS[option.value] ?? "🏳"}
                      </span>
                      <span>
                        {option.label}
                      </span>
                    </span>
                    {isActive ? (
                      <span className="text-xs uppercase text-slate-500">
                        {t("common.active")}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </SelectionModal>
      )}
    </>
  );
}
